/**
 * Contract of the client-side planning store (implemented in
 * src/client/store/planningStore.ts, consumed by all views).
 *
 * The store holds one planning (possibly a date window of it), applies
 * ChangeSets coming back from POST /changes and from the SSE stream, and lets
 * views subscribe with selectors (useSyncExternalStore). State objects are
 * immutable: every update produces a new state object and new Map instances for
 * the collections that changed, so selectors can compare by reference.
 */
import type {
  AssignmentDto,
  CalendarDto,
  ChangeOp,
  ChangeSet,
  DependencyDto,
  EditLockDto,
  PlanningSummaryDto,
  ResourceDto,
  TaskDto,
} from '../../shared/api.js';
import type { PlanningSettings } from '../../shared/model.js';
import type { LocalMinutes } from '../../shared/time.js';

export interface PlanningStoreState {
  planningId: string | null;
  planning: PlanningSummaryDto | null;
  settings: PlanningSettings | null;
  revision: number;
  /**
   * 'loading' only for the first load of a planning: re-fetching an already
   * loaded planning (extendWindow, reload) keeps 'ready' so views stay mounted,
   * and sets `reloading` instead.
   */
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** True while a loaded planning is re-fetched in the background (for a small progress indicator). */
  reloading: boolean;
  /** Last load error; after a failed background reload the status stays 'ready' (the old data is kept). */
  error: string | null;
  /** Loaded window (null = unbounded). */
  window: { from: LocalMinutes | null; to: LocalMinutes | null };
  tasks: Map<number, TaskDto>;
  /** Task uids in row order (seq). */
  taskOrder: number[];
  /** Child uids per parent uid (null = top level), in row order. */
  children: Map<number | null, number[]>;
  /** Parent uid per task uid (null = top level). */
  parent: Map<number, number | null>;
  /** Key: `${predUid}>${succUid}` */
  dependencies: Map<string, DependencyDto>;
  assignments: Map<number, AssignmentDto>;
  assignmentsByTask: Map<number, number[]>;
  assignmentsByResource: Map<number, number[]>;
  resources: Map<number, ResourceDto>;
  /** Resource uids in row order. */
  resourceOrder: number[];
  calendars: Map<number, CalendarDto>;
  editLock: EditLockDto | null;
  /** True when the user may not edit (viewer role or ProjectLibre edit lock). */
  readOnly: boolean;
  /** Pending change requests (for a saving indicator). */
  pending: number;
  /** Last ChangeSet applied (for "undo"-style toasts / highlighting). */
  lastChange: ChangeSet | null;
}

export interface PlanningStore {
  getState(): PlanningStoreState;
  subscribe(listener: () => void): () => void;
  /** Loads a planning (window optional) and opens the SSE stream. */
  load(planningId: string, window?: { from: LocalMinutes | null; to: LocalMinutes | null }): Promise<void>;
  /** Widens the loaded window (e.g. "toon historie"); a background reload (see `reloading`). */
  extendWindow(from: LocalMinutes | null, to: LocalMinutes | null): Promise<void>;
  /** Sends ops to the server and applies the returned ChangeSet. Rejects with an ApiError-like Error on failure (state unchanged). */
  change(ops: ChangeOp[]): Promise<ChangeSet>;
  /** Applies a ChangeSet (from POST /changes or SSE); ignores stale/own echoes by revision/clientId. */
  applyChangeSet(cs: ChangeSet): void;
  /** Re-fetches everything (used after an SSE "reload" event or a revision gap); a background reload when already loaded. */
  reload(): Promise<void>;
  /** Closes SSE, clears state. */
  reset(): void;
  readonly clientId: string;
}

export const dependencyKey = (predUid: number, succUid: number): string => `${predUid}>${succUid}`;
